/**
 * RealGameApi — implements GameApi over a live WebSocket connection.
 * Used when VITE_WS_ENDPOINT is set (must be a full wss:// or ws:// URL).
 */

import type { GameApi, GameEventMap, Unsubscribe } from '../GameApi';
import type {
  BetPlaceRequest,
  BetCancelRequest,
  BetCashoutRequest,
  ProfileUpdateRequest,
  BetHistoryGetRequest,
  LeaderboardGetRequest,
  StreamerJoinRequest,
  StreamerLeaveRequest,
  WsRequest,
} from '../types';
import { ClientMsg, ServerMsg } from '../types';
import { WebSocketManager } from './WebSocketManager';
import { MessageRouter } from './MessageRouter';

const SESSION_COOKIE = 'mp_session';
const SESSION_LS_KEY = 'mp_session_token';

export class RealGameApi implements GameApi {
  private wsManager: WebSocketManager;
  private router: MessageRouter;
  private listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  constructor(private wsUrl: string) {
    const tokenUrl = this.buildAuthUrl(wsUrl);
    this.wsManager = new WebSocketManager(tokenUrl);
    this.router = new MessageRouter();
    this.wireRouterToListeners();
    this.wireWsManagerEvents();
  }

  get isConnected(): boolean {
    return this.wsManager.isConnected;
  }

  connect(): void {
    this.ensureSessionToken();
    this.wsManager.connect();
  }

  disconnect(): void {
    this.wsManager.disconnect();
    this.router.destroy();
  }

  // ── Client → Server actions ────────────────────────

  placeBet(req: BetPlaceRequest): void {
    this.send(ClientMsg.BetPlace, req);
  }

  cancelBet(req: BetCancelRequest): void {
    this.send(ClientMsg.BetCancel, req);
  }

  cashout(req: BetCashoutRequest): void {
    this.send(ClientMsg.BetCashout, req);
  }

  updateProfile(req: ProfileUpdateRequest): void {
    this.send(ClientMsg.ProfileUpdate, req);
  }

  getPlayerStats(): void {
    this.send(ClientMsg.PlayerStatsGet, {});
  }

  getBetHistory(req?: BetHistoryGetRequest): void {
    this.send(ClientMsg.PlayerBetHistoryGet, req ?? {});
  }

  getLeaderboard(req?: LeaderboardGetRequest): void {
    this.send(ClientMsg.LeaderboardGet, req ?? {});
  }

  joinStreamerRoom(req: StreamerJoinRequest): void {
    this.send(ClientMsg.StreamerJoin, req);
  }

  leaveStreamerRoom(req?: StreamerLeaveRequest): void {
    this.send(ClientMsg.StreamerLeave, req ?? {});
  }

  getStreamerList(): void {
    this.send(ClientMsg.StreamerListGet, {});
  }

  // ── Event subscription ─────────────────────────────

  on<K extends keyof GameEventMap>(event: K, handler: GameEventMap[K]): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    const set = this.listeners.get(event)!;
    set.add(handler as (...args: unknown[]) => void);
    return () => { set.delete(handler as (...args: unknown[]) => void); };
  }

  // ── Internals ──────────────────────────────────────

  private send<P>(messageType: string, payload: P): void {
    const envelope: WsRequest<P> = {
      messageType: messageType as WsRequest['messageType'],
      correlationId: crypto.randomUUID(),
      payload,
    };
    this.wsManager.send(JSON.stringify(envelope));
  }

  private wireWsManagerEvents(): void {
    this.wsManager.on('message', (data: string) => {
      this.router.route(data);
    });

    this.wsManager.on('open', () => {
      this.emitLocal('connection:status', { connected: true, status: 'open' });
    });

    this.wsManager.on('close', (code: number, reason: string) => {
      this.emitLocal('connection:status', {
        connected: false,
        status: 'closed',
        closeCode: code,
        closeReason: reason,
      });
    });

    this.wsManager.on('reconnecting', (meta) => {
      this.emitLocal('connection:status', {
        connected: false,
        status: 'reconnecting',
        closeCode: meta.closeCode,
        closeReason: meta.closeReason,
        attempt: meta.attempt,
        delayMs: meta.delayMs,
      });
    });

    this.wsManager.on('error', (_ev: Event) => {
      // WebSocket errors are followed by a close event; reconnection is in WsManager
    });
  }

  private wireRouterToListeners(): void {
    const serverEvents: Array<{ msgType: string; eventKey: keyof GameEventMap }> = [
      { msgType: ServerMsg.SessionInit, eventKey: 'session:init' },
      { msgType: ServerMsg.RoundState, eventKey: 'round:state' },
      { msgType: ServerMsg.Tick, eventKey: 'tick' },
      { msgType: ServerMsg.RoundCrashed, eventKey: 'round:crashed' },
      { msgType: ServerMsg.AutoCashout, eventKey: 'auto:cashout' },
      { msgType: ServerMsg.BalanceUpdate, eventKey: 'balance:update' },
      { msgType: ServerMsg.PlayerStats, eventKey: 'player:stats' },
      { msgType: ServerMsg.PlayerBetHistoryData, eventKey: 'player:betHistory:data' },
      { msgType: ServerMsg.LeaderboardData, eventKey: 'leaderboard:data' },
      { msgType: ServerMsg.PlayersOnline, eventKey: 'players:online' },
      { msgType: ServerMsg.StreamerRoomUpdate, eventKey: 'streamer:roomUpdate' },
      { msgType: ServerMsg.StreamerList, eventKey: 'streamer:list' },
      { msgType: ServerMsg.BetPlaceAck, eventKey: 'bet:place' },
      { msgType: ServerMsg.BetCancelAck, eventKey: 'bet:cancel' },
      { msgType: ServerMsg.BetCashoutAck, eventKey: 'bet:cashout' },
    ];

    for (const { msgType, eventKey } of serverEvents) {
      this.router.onMessage(msgType, (payload) => {
        // Payload shape is validated by the server — dispatch to typed handlers
        this.dispatchEvent(eventKey, payload);
      });
    }

    this.router.onError((error, _correlationId) => {
      this.dispatchEvent('error', error);
    });
  }

  private emitLocal<K extends keyof GameEventMap>(
    event: K,
    ...args: Parameters<GameEventMap[K]>
  ): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        (handler as (...a: unknown[]) => void)(...args);
      } catch (e) {
        console.error(`[RealGameApi] Error in handler for '${event}':`, e);
      }
    }
  }

  /** Untyped internal dispatch for dynamic router→handler bridge */
  private dispatchEvent(event: string, payload: unknown): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const handler of set) {
      try {
        (handler as (p: unknown) => void)(payload);
      } catch (e) {
        console.error(`[RealGameApi] Error in handler for '${event}':`, e);
      }
    }
  }

  /**
   * Ensure a session token exists. Store in both localStorage and cookie.
   * The BE extracts the token from the `Cookie: mp_session=<TOKEN>` header.
   */
  private ensureSessionToken(): string {
    let token = this.getStoredToken();
    if (!token) {
      token = crypto.randomUUID();
      this.persistToken(token);
    }
    return token;
  }

  private getStoredToken(): string | null {
    try {
      return localStorage.getItem(SESSION_LS_KEY);
    } catch {
      return null;
    }
  }

  private persistToken(token: string): void {
    try {
      localStorage.setItem(SESSION_LS_KEY, token);
    } catch { /* no-op */ }

    // Set cookie for WS upgrade request
    document.cookie = `${SESSION_COOKIE}=${encodeURIComponent(token)}; path=/; SameSite=Lax`;
  }

  /**
   * Resolve the configured URL to an absolute ws(s):// URL.
   * Handles:
   *   - Relative paths like "/ws" → ws(s)://current-host/ws
   * Appends session token as a query param for browsers that
   * don't send cookies on cross-origin WS upgrade requests.
   * The base URL must be a full wss:// or ws:// URL (set via VITE_WS_ENDPOINT).
   */
  private buildAuthUrl(base: string): string {
    const token = this.ensureSessionToken();
    const url = new URL(base, window.location.href);
    if (!url.searchParams.get('token')) {
      url.searchParams.set('token', token);
    }

    const streamer = this.readStreamerParam();
    if (streamer && !url.searchParams.get('streamer')) {
      url.searchParams.set('streamer', streamer);
    }

    return url.toString();
  }

  private readStreamerParam(): string | null {
    const streamer = new URLSearchParams(window.location.search).get('streamer');
    if (!streamer) return null;
    const trimmed = streamer.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
}
